import type { Grants, PermissionAccess } from '../dto/tenant-role.dto.js'
import type { TenantFeature } from '../enums/tenant-feature.enum.js'

/**
 * What a company role can be given (docs/custom-roles.spec.md, "Permissions"),
 * in the order the role editor lists it. The grants themselves are
 * `grantsSchema` in `lib/src/dto/tenant-role.dto.ts`; this is what each one
 * means to the person choosing it.
 */

/** The switches: each a yes or no under one area, needing that area's Edit. */
export const SWITCH_KEYS = ['deleteEvents', 'salesPriceList', 'salesRefunds'] as const
export type SwitchKey = (typeof SWITCH_KEYS)[number]

export type PermissionArea = Exclude<keyof Grants, SwitchKey>

export interface PermissionAreaInfo {
  key: PermissionArea
  label: string
  /** What the area covers, in the person's words. */
  description: string
  /** `venue` applies at the role's venues; `company` across the company. */
  scope: 'venue' | 'company'
  /** The levels the area has beyond None. */
  levels: readonly PermissionAccess[]
  /** The company needs one of these features for the area to mean anything; empty when it needs none. */
  features: readonly TenantFeature[]
}

export const PERMISSION_CATALOG: readonly PermissionAreaInfo[] = [
  {
    key: 'events',
    label: 'Events',
    description:
      'Events, the calendar and dashboard, floor plans, timelines and documents, and the clients and vendors on an event.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'clients',
    label: 'Clients',
    description: 'Couples’ accounts: looking them up, inviting them and deactivating them.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'spaces',
    label: 'Spaces & layouts',
    description: 'Rooms, their drawings and versions, saved layouts and tracks.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'furniture',
    label: 'Furniture & inventory',
    description: 'The furniture catalog and its prices, groups, and stock counts.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'venueSetup',
    label: 'Venue setup',
    description: 'The venue’s photo, schedule, timeline templates and share link.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'vendors',
    label: 'Vendors',
    description: 'Which vendors may serve the venue, and adding new ones.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'marketing',
    label: 'Marketing',
    description: 'The venue’s pricing page.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: ['marketing'],
  },
  {
    key: 'leadStats',
    label: 'Leads & pipeline',
    description:
      'The pipeline from ad spend to the event, and lead numbers from GoHighLevel. Edit adds what each lead number is made of at the venue.',
    scope: 'venue',
    levels: ['view', 'edit'],
    // Every company has the pipeline; the lead numbers' routes still need GoHighLevel.
    features: [],
  },
  {
    key: 'sales',
    label: 'Sales',
    description:
      'Proposals, contracts and payments. View sees them and the price list; Edit builds and sends packets, invoices and amendments, countersigns, records outside payments and returns security deposits.',
    scope: 'venue',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'ads',
    label: 'Ads',
    description: 'Ad numbers and spend, including the cost of each lead, tour and booking.',
    scope: 'venue',
    levels: ['view'],
    features: ['ads'],
  },
  {
    key: 'people',
    label: 'People',
    description:
      'The staff list. Edit adds inviting, changing and deactivating staff, but never beyond the person’s own access.',
    scope: 'company',
    levels: ['view', 'edit'],
    features: [],
  },
  {
    key: 'venues',
    label: 'Venues',
    description: 'Adding venues, changing their name and address, and deleting them.',
    scope: 'company',
    levels: ['edit'],
    features: [],
  },
  {
    key: 'integrations',
    label: 'Integrations',
    description: 'GoHighLevel and Google Ads connections, and what lead stats count.',
    scope: 'company',
    levels: ['edit'],
    features: ['gohighlevel', 'ads'],
  },
  {
    key: 'imports',
    label: 'Imports',
    description: 'Importing from Prism and Social Tables, and removing what was imported.',
    scope: 'company',
    levels: ['edit'],
    features: [],
  },
]

/** The switch under Events. */
export const DELETE_EVENTS_SWITCH = {
  label: 'Delete events',
  description: 'Deleting an event, and clearing a venue’s events. Neither can be undone.',
} as const

/** The switches under Sales: both move money (docs/sales.spec.md, "Who does what"). */
export const SALES_PRICE_LIST_SWITCH = {
  label: 'Price list',
  description: 'Changing prices, tax and service charge.',
} as const

export const SALES_REFUNDS_SWITCH = {
  label: 'Refunds',
  description: 'Refunding payments.',
} as const

/** What each switch is called and means, to the person choosing it. */
export const SWITCH_INFO: Readonly<Record<SwitchKey, { label: string; description: string }>> = {
  deleteEvents: DELETE_EVENTS_SWITCH,
  salesPriceList: SALES_PRICE_LIST_SWITCH,
  salesRefunds: SALES_REFUNDS_SWITCH,
}

/** Which switches sit under which area. Each needs its area at Edit, and dropping the area below Edit turns them off. */
export const AREA_SWITCHES: Readonly<Partial<Record<PermissionArea, readonly SwitchKey[]>>> = {
  events: ['deleteEvents'],
  sales: ['salesPriceList', 'salesRefunds'],
}

export const VENUE_MANAGER_NAME = 'Venue manager'

/** What an organization admin is called where a person's role is named. Not a role: it is fixed, and can do everything. */
export const ORGANIZATION_ADMIN_NAME = 'Organization admin'

/**
 * The built-in Venue manager: what venue admins can do today, area by area
 * (docs/custom-roles.spec.md, "Permissions"), so nothing changes for a company
 * until it edits the role. Ads View is one addition: ad costs follow Ads, and
 * venue managers see the cost of a lead in their lead stats. Sales is another,
 * with the price list but not refunds: refunds send money back, so they stay
 * the organization admin's until a role is given them.
 */
export const VENUE_MANAGER_DEFAULTS: Grants = {
  events: 'edit',
  clients: 'edit',
  spaces: 'edit',
  furniture: 'edit',
  venueSetup: 'edit',
  vendors: 'edit',
  marketing: 'edit',
  leadStats: 'edit',
  sales: 'edit',
  ads: 'view',
  people: 'none',
  venues: 'none',
  integrations: 'none',
  imports: 'none',
  deleteEvents: true,
  salesPriceList: true,
  salesRefunds: false,
}

/** Everything, at the highest level each area has: what an organization admin can do. Not a role anyone edits. */
export const ALL_GRANTS: Grants = {
  events: 'edit',
  clients: 'edit',
  spaces: 'edit',
  furniture: 'edit',
  venueSetup: 'edit',
  vendors: 'edit',
  marketing: 'edit',
  leadStats: 'edit',
  sales: 'edit',
  ads: 'view',
  people: 'edit',
  venues: 'edit',
  integrations: 'edit',
  imports: 'edit',
  deleteEvents: true,
  salesPriceList: true,
  salesRefunds: true,
}

/** Where a new role starts: nothing, so whatever it can do was chosen. */
export const NO_GRANTS: Grants = {
  events: 'none',
  clients: 'none',
  spaces: 'none',
  furniture: 'none',
  venueSetup: 'none',
  vendors: 'none',
  marketing: 'none',
  leadStats: 'none',
  sales: 'none',
  ads: 'none',
  people: 'none',
  venues: 'none',
  integrations: 'none',
  imports: 'none',
  deleteEvents: false,
  salesPriceList: false,
  salesRefunds: false,
}
